import React from "react"
import toast, { Toaster as HotToaster } from 'react-hot-toast'
import ToastInfo from "./ToastInfo"
import ToastSuccess from "./ToastSuccess"
import ToastFail from "./ToastFail"
import {
  normalizeToastType,
  TOASTER_CONTAINER_STYLE,
  type ToastInputType,
  type ToastType,
} from "./toastNotificationStyles"

export type { ToastType } from "./toastNotificationStyles"

// Maximum number of toasts to show at once
const MAX_TOASTS = 3;

// Track active toast IDs in order
const activeToastIds: string[] = [];
const activeToastMessages = new Map<string, string>();

/**
 * TriggerToast function
 *
 * This function triggers a toast notification using react-hot-toast.
 * Limits the number of visible toasts to 3 maximum.
 *
 * @param {string | string[]} content - The message to be displayed in the toast notification. Can be a string or array of strings.
 * @param {number} [duration=5000] - Optional duration (in milliseconds) for which the toast should be visible. Defaults to 5000 ms.
 * @param {ToastInputType} [type='info'] - Optional visual style for the toast.
 *
 * @note The `TriggerToast` function works in conjunction with the `<Toaster />` component to display notifications.
 */
export const TriggerToast = (content: string | string[], duration: number = 5000, type: ToastInputType = 'info') => {
  const message = Array.isArray(content) ? content.join(', ') : content;

  if (Array.from(activeToastMessages.values()).includes(message)) {
    return;
  }

  // If we're at the limit, dismiss the oldest toast
  if (activeToastIds.length >= MAX_TOASTS) {
    const oldestId = activeToastIds.shift();
    if (oldestId) {
      toast.dismiss(oldestId);
      activeToastMessages.delete(oldestId);
    }
  }

  const normalizedType: ToastType = normalizeToastType(type);
  let ToastComponent = ToastInfo;
  if (normalizedType === 'success') {
    ToastComponent = ToastSuccess;
  } else if (normalizedType === 'fail') {
    ToastComponent = ToastFail;
  }

  // Create the toast and track its ID
  const toastId = toast.custom(<ToastComponent message={message} />, {
    duration,
    position: 'bottom-center',
  });

  // Add to active list
  activeToastIds.push(toastId);
  activeToastMessages.set(toastId, message);

  // Clean up the ID from the list when toast auto-dismisses
  setTimeout(() => {
    const index = activeToastIds.indexOf(toastId);
    if (index > -1) {
      activeToastIds.splice(index, 1);
    }
    activeToastMessages.delete(toastId);
  }, duration + 100); // Add 100ms buffer
}

/**
 * Toaster component
 *
 * This component provides the toast notification container using react-hot-toast.
 * Should be included once at the app layout level.
 *
 * @returns JSX.Element - The `Toaster` component configured to match app styling.
 */
const Toaster: React.FC = () => {
  return (
    <HotToaster
      position="bottom-center"
      containerStyle={TOASTER_CONTAINER_STYLE}
      containerClassName="ui-toast-container"
      toastOptions={{
        duration: 5000,
        className: 'ui-toast',
      }}
    />
  );
}

export default Toaster
